import React, { PropsWithChildren, ReactNode } from 'react'
import clsx from 'clsx'
import { SROnly as SROnlyStyles, Stepper as StepperStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import { CommonProps } from '../helperTypes'

type Props = PropsWithChildren<{
  /** The active step (zero based index) */
  activeStep?: number
  /**
   * Specify the label for the root element
   * @default "progress"
   */
  'aria-label'?: string
  /** If `true`, sets the stepper to an horizontal orientation */
  horizontal?: boolean
  /** If `true`, user can navigate freely to any step */
  nonLinear?: boolean
  /** Callback invoked when clicking on a step header to navigate */
  onStepClick?: (index: number) => void
}>

type DeprecatedProps = {
  onClick?: (value: any) => void
}

export type StepperProps = Props & DeprecatedProps & CommonProps

const useStyles = createUseStyles(StepperStyles, 'stepper')
const useSROnlyStyles = createUseStyles(SROnlyStyles, 'sronly')

const Stepper = (props: StepperProps) => {
  const {
    activeStep = 0,
    'aria-label': ariaLabel = 'progress',
    children,
    className,
    horizontal = false,
    onClick,
    onStepClick,
    nonLinear,
    ...rest
  } = props
  useDeprecatedProps('Stepper', { onClick: 'onStepClick' }, props)
  const classes = useStyles()

  const handleClick = React.useCallback(
    (index: number) => {
      if (onClick) {
        onClick(index)
      } else if (onStepClick) {
        onStepClick(index)
      }
    },
    [onClick, onStepClick],
  )

  const stepChildren = React.useMemo(() => {
    let index = 0
    return React.Children.map(children, (child, idx) => {
      if (!React.isValidElement(child)) {
        return null
      }
      if (child.type === React.Fragment) {
        console.warn(
          "[AIRBUS-DS] The Stepper component doesn't accept a Fragment as a child. Consider providing an array instead.",
        )
      } else if (child.type !== Step) {
        console.warn('[AIRBUS-DS] The Stepper component only accept Step as children.')
      }
      return React.cloneElement<any>(child, {
        active: index === activeStep,
        disabled: !nonLinear && index > activeStep,
        done: index < activeStep,
        index: index++,
        onClick: handleClick,
      })
    })
  }, [activeStep, children, handleClick, nonLinear])

  return (
    <ol
      aria-label={ariaLabel}
      className={clsx(className, classes.root, {
        [classes._vertical]: !horizontal,
      })}
      {...rest}
    >
      {stepChildren}
    </ol>
  )
}

export default Stepper

type BaseStepProps = PropsWithChildren<{
  /** Callback invoked when step is clicked */
  onClick?: (index: number) => void
  /** Set to `true` for sub steps */
  subStep?: boolean
}>
export type StepProps = BaseStepProps & CommonProps

export const Step = (props: StepProps) => {
  const { active, children, className, disabled, done, index = 0, onClick, subStep, ...rest } = props
  const classes = useStyles()
  const childrenWithProps = React.useMemo(() => {
    return React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) {
        return null
      }
      if (child.type === StepHeader) {
        return React.cloneElement<StepHeaderProps>(child as React.FunctionComponentElement<StepHeaderProps>, {
          ...rest,
          ...(!active &&
            onClick && {
              onClick: (evt: React.SyntheticEvent) => {
                evt.stopPropagation()
                onClick(index)
              },
            }),
          disabled,
          index,
          noNumber: subStep,
        })
      } else return child
    })
  }, [active, children, disabled, index, onClick, rest, subStep])

  return (
    <li
      className={clsx(className, classes.step, {
        [classes.step_complete]: done === true,
        [classes.step_current]: active === true,
      })}
      aria-current={active ? 'step' : undefined}
    >
      {childrenWithProps}
    </li>
  )
}

type HeaderProps = PropsWithChildren<{
  /** Current step */
  active?: boolean
  /** Disabled state */
  disabled?: boolean
  /** Completed step */
  done?: boolean
  /** The number to display */
  index?: number
  /** The icon to display */
  icon?: ReactNode
  /** Hide number/icon (for sub steps) */
  noNumber?: boolean
  /** onClick handler */
  onClick?: (event: React.SyntheticEvent<HTMLElement>) => void
}>
export type StepHeaderProps = HeaderProps & CommonProps

export const StepHeader = (props: StepHeaderProps) => {
  const { active, children, className, disabled, done, icon, index = 0, noNumber = false, onClick, ...rest } = props
  const classes = useStyles()
  const srOnlyClasses = useSROnlyStyles()

  const handleClick = (evt: React.MouseEvent<HTMLElement>) => {
    if (disabled) {
      evt.stopPropagation()
      return
    }
    onClick && onClick(evt)
  }
  const handleKeyDown = (evt: React.KeyboardEvent<HTMLElement>) => {
    const isSpaceOrEnter = [' ', 'Enter'].includes(evt.key)
    if (disabled && isSpaceOrEnter) {
      evt.stopPropagation()
      return
    }
    if (!isSpaceOrEnter) return
    evt.preventDefault()
    onClick && onClick(evt)
  }

  return (
    <div
      role='link'
      aria-disabled={disabled ? true : undefined}
      tabIndex={disabled ? -1 : 0}
      className={clsx(className, classes.stepHeader)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      {...rest}
    >
      {!noNumber && (
        <span className={classes.stepNumber} aria-hidden='true'>
          {icon ? icon : index + 1}
        </span>
      )}
      {done && <span className={srOnlyClasses.root}>Completed: </span>}
      {active && <span className={srOnlyClasses.root}>Current: </span>}
      <span>{children}</span>
    </div>
  )
}

export const StepContent = (props: PropsWithChildren<CommonProps>) => {
  const { children, className, ...rest } = props
  const classes = useStyles()

  return (
    <div className={clsx(className, classes.stepContent)} {...rest}>
      {children}
    </div>
  )
}

type SubStepsProps = PropsWithChildren<{
  /** The active sub step (zero based index) */
  activeSubStep?: number
  /** Callback invoked when clicking on a sub step header to navigate */
  onSubStepClick?: (index: number) => void
}>
export type StepSubStepsProps = SubStepsProps & CommonProps

export const StepSubSteps = (props: StepSubStepsProps) => {
  const { activeSubStep = 0, children, className, onSubStepClick, ...rest } = props
  const classes = useStyles()
  const subStepChildren = React.useMemo(() => {
    let index = 0
    return React.Children.map(children, (child, idx) => {
      if (!React.isValidElement(child)) {
        return null
      }
      if (child.type === React.Fragment) {
        console.warn(
          "[AIRBUS-DS] The StepSubSteps component doesn't accept a Fragment as a child. Consider providing an array instead.",
        )
      } else if (child.type !== Step) {
        console.warn('[AIRBUS-DS] The StepSubSteps component only accept Step as children.')
      }
      return React.cloneElement<any>(child, {
        active: index === activeSubStep,
        disabled: index > activeSubStep,
        done: index < activeSubStep,
        index: index++,
        onClick: onSubStepClick,
        subStep: true,
      })
    })
  }, [activeSubStep, children, onSubStepClick])

  return (
    <ol className={clsx(className, classes.stepSubsteps)} {...rest}>
      {subStepChildren}
    </ol>
  )
}
